<script setup>
import { computed, onMounted, ref } from "vue";
import { Icon, Input, ProfilePicture, Textarea, Button } from "@components";

import { getPrice, showDurationUnit } from "@utils";

import moment from "moment";

import { useService } from "@store/service.js";
const $service = useService();

import { useStaff } from "@store/staff.js";
const $staff = useStaff();

onMounted(() => {
  $service.fetchData();
  $staff.fetchData();
});

import CenterLayout from "./CenterLayout.vue";

const selectedService = computed(() => {
  const firstService = $service.getServices[0];
  return (
    firstService || {
      service_id: 1,
      name: "Dental Examinations",
      category_id: 1,
      status: "publish",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      meta: {
        color: "#2E90FA",
        price: 120,
        duration: "75",
        duration_unit: "minute",
        price_type: "fixed",
      },
      prices: [],
      staffs: [],
    }
  );
});

const selectedStaff = computed(() => {
  const firstStaff = $staff.getStaffs[0];
  return (
    firstStaff || {
      staff_id: 1,
      display_name: "John Doe",
    }
  );
});

import { useChatCustomizer } from "@store/chatCustomizer";
const $chatCustomizer = useChatCustomizer();

import { useSettings } from "@store/settings";
const $settings = useSettings();

const default_banner =
  prebook_admin?.url?.assets + "images/chat-customizer-top-banner.jpeg";
</script>

<template>
  <CenterLayout>
    <div
      class="h-[66px] w-[297px] rounded-md overflow-hidden m-auto mb-10"
      :style="{
        'background-color': $settings.customizer?.design?.brand_color,
      }"
    >
      <img
        v-if="$settings.settings.chat_widget.banner.image"
        :src="$settings.settings.chat_widget.banner.image"
        class="cursor-pointer"
        alt=""
      />
      <img
        v-else
        :src="default_banner"
        class="cursor-pointer"
        alt=""
        style="object-fit: cover; width: 100%; height: -webkit-fill-available"
      />
    </div>

    <div class="mb-10 text-center font-semibold text-lg">...</div>
    <div
      class="rounded-[14px] p-5 flex flex-col gap-5 bg-white w-[381px] m-auto"
      style="box-shadow: 0px 50px 76px -2px rgba(11, 17, 29, 0.1)"
    >
      <div class="flex flex-col gap-3">
        <div class="text-sm font-medium">Questions</div>
        <Textarea
          v-model="$settings.settings.chat_widget.overview.question"
        ></Textarea>
      </div>

      <div>
        <div class="flex items-center gap-4 py-3 px-5">
          <div class="flex items-center gap-2">
            <Icon name="receiptCheck" class="text-[#039855]" size="30px" />
            <div class="text-[#101828] text-sm font-medium">
              Your Appointment is confirmed
            </div>
          </div>
          <Icon
            name="xClose"
            size="40px"
            class="p-[10px] cursor-pointer rounded-full bg-[#F2F4F7]"
          />
        </div>

        <div class="p-5 border-y border-[#EAECF0]">
          <div class="flex items-center gap-3">
            <ProfilePicture
              :name="selectedService.name"
              imgClass="!rounded-md !w-[52px] !h-[52px]"
              textClass="!rounded-md !w-[52px] !h-[52px]"
            />
            <div>
              <div class="text-[#101828] text-lg font-semibold">
                {{ selectedService.name }}
              </div>
              <div class="text-[#475467] text-sm">
                {{ selectedStaff.display_name }}
              </div>
            </div>
            <div class="text-[#101828] text-lg font-semibold ml-auto">
              {{ getPrice(selectedService.meta.price) }}
            </div>
          </div>
        </div>

        <div class="mt-5 mb-4 px-5 flex flex-col gap-5">
          <div
            class="flex items-center justify-between gap-3 text-[#101828] text-sm"
          >
            <div>Duration</div>
            <div>
              {{
                showDurationUnit(
                  selectedService.meta.duration,
                  selectedService.meta.duration_unit
                ) || "0 min"
              }}
            </div>
          </div>
          <div
            class="flex items-center justify-between gap-3 text-[#101828] text-sm"
          >
            <div>Time</div>
            <div>
              {{
                moment(selectedService.meta.time).format("DD MMM YYYY h:mm A")
              }}
            </div>
          </div>
          <div
            class="flex items-center justify-between gap-3 text-[#101828] text-sm"
          >
            <div>Location</div>
            <div class="max-w-[50%] text-end">
              6391 Elgin St. Celina, Delaware 10299
            </div>
          </div>
        </div>

        <div class="flex gap-5 px-5 py-3">
          <!-- <Button type="secondary_gray" size="md" radius="square" class="w-full">Pay Later</Button> -->
          <Button type="secondary_gray" size="md" radius="square" class="w-full"
            >Pay Now</Button
          >
        </div>

        <div class="flex gap-5 px-5 py-3">
          <Button
            type="secondary_gray"
            size="md"
            radius="square"
            class="w-[170px]"
          >
            <template #before>
              <Icon name="printer" size="20px" />
            </template>
            Print
          </Button>
          <Button
            type="secondary_gray"
            size="md"
            radius="square"
            class="w-full"
          >
            <template #before>
              <Icon name="calendar" size="20px" />
            </template>
            Add to Calendar
          </Button>
        </div>

        <div
          class="text-[#101828] text-sm py-[10px] text-center bg-[#F2F4F7] mt-[34px]"
        >
          Questions? Contact our
          <span class="font-semibold">Customer Support</span>
        </div>
      </div>
    </div>

    <template #sidebar>
      <div class="mt-[44px]">
        <div class="text-[#101828] text-xs font-semibold">Pro Tips</div>
        <div class="text-[#101828] text-lg font-semibold mt-1">Overview</div>
        <ul class="pl-5 mt-1 text-sm text-[#475467] list-disc">
          <li>Set your overview</li>
        </ul>
      </div>
    </template>
  </CenterLayout>
</template>

<style></style>
